import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Navbar from "react-bootstrap/Navbar";
import Nav from "react-bootstrap/Nav";
import Container from "react-bootstrap/Container";
import { AiOutlineHome, AiOutlineInfoCircle, AiOutlineFundProjectionScreen } from "react-icons/ai";
import LanguageSwitcher from "../LanguageSwitcher";
import { useTranslation } from "react-i18next";
import ThemeSwitcher from "../ThemeSwitcher";
import "./Navbar.css";

function NavBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expand, updateExpanded] = useState(false);
  const location = useLocation();
  const navRef = useRef(null);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

  useEffect(() => {
    function handleThemeChange() {
      const newTheme = localStorage.getItem("theme") || "light";
      setTheme(newTheme);
    }
    window.addEventListener("themeChanged", handleThemeChange);
    return () => window.removeEventListener("themeChanged", handleThemeChange);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (expand && navRef.current && !navRef.current.contains(event.target)) {
        updateExpanded(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [expand]);

  useEffect(() => {
    // Apply or remove blur class based on expand state
    if (expand) {
      document.body.classList.add("content-blur", "navbar-expanded");
    } else {
      document.body.classList.remove("content-blur", "navbar-expanded");
    }
  }, [expand]);

  useEffect(() => {
    // Handle window resize to reset expand state on large screens
    function handleResize() {
      if (window.innerWidth >= 992) { // Bootstrap 'lg' breakpoint
        updateExpanded(false); // Reset expand state
        document.body.classList.remove("content-blur", "navbar-expanded"); // Remove blur classes
      }
    }

    // Run on mount and when window is resized
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.scrollTo(0, 0);
    } else {
      navigate("/");
    }
  };

  return (
    <Navbar ref={navRef} expanded={expand} fixed="top" expand="lg" className="navbar">
      <Container>
<Navbar.Brand 
  as={Link} 
  to="/" 
  onClick={handleLogoClick}
  className="logo-container"
  aria-label="Accueil"
>
  <img
    src={theme === "light" ? "/logo_RM_light_square.webp" : "/logo_RM_dark_square.webp"}
    alt="Logo Reda Mdair"
    fetchpriority="high" // Indique au navigateur que c'est l'image la plus importante
    width="75"  // Définit une largeur fixe pour éviter le CLS (Cumulative Layout Shift)
    height="75" // Définit une hauteur fixe
    className="img-fluid logo-img"
  />
</Navbar.Brand>
        <Navbar.Toggle
          aria-controls="responsive-navbar-nav"
          onClick={() => updateExpanded(!expand)}
          aria-label={t("navbar.toggle") || "Toggle navigation"}
        >
          <span></span>
          <span></span>
          <span></span>
        </Navbar.Toggle>
        <Navbar.Collapse id="responsive-navbar-nav">
  {/* BLOC CENTRAL : Rubriques + ThemeSwitcher */}
  <Nav className="mx-auto d-flex align-items-center">
    <Nav.Item>
      <Nav.Link
        as={Link}
        to="/"
        className={`nav-link-custom ${location.pathname === "/" ? "active" : ""}`}
        onClick={() => updateExpanded(false)}
      >
        <AiOutlineHome aria-hidden="true" />
        <span>{t("navbar.home")}</span>
      </Nav.Link>
    </Nav.Item>
    <Nav.Item>
      <Nav.Link
        as={Link}
        to="/projects"
        className={`nav-link-custom ${location.pathname === "/projects" ? "active" : ""}`}
        onClick={() => updateExpanded(false)}
      >
        <AiOutlineFundProjectionScreen aria-hidden="true" />
        <span>{t("navbar.projects")}</span>
      </Nav.Link>
    </Nav.Item>
    <Nav.Item>
      <Nav.Link
        as={Link}
        to="/about"
        className={`nav-link-custom ${location.pathname === "/about" ? "active" : ""}`}
        onClick={() => updateExpanded(false)}
      >
        <AiOutlineInfoCircle aria-hidden="true" />
        <span>{t("navbar.about")}</span>
      </Nav.Link>
    </Nav.Item>
    
    {/* On garde le ThemeSwitcher soudé aux rubriques pour le centrage */}
    <Nav.Item className="theme-switcher-wrapper">
      <ThemeSwitcher />
    </Nav.Item>
  </Nav>

  {/* BLOC DROITE : Uniquement la langue */}
  <Nav className="ms-auto language-nav-wrapper">
    <Nav.Item>
      <LanguageSwitcher />
    </Nav.Item>
  </Nav>
</Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default NavBar;